import React, { useEffect, useState } from 'react'
import { Button, Center, Group, Modal, Text } from '@mantine/core'
import { DownloadIcon } from '@radix-ui/react-icons'

import { CourtReporterExhibit } from '@/apiClient'
import { fileExtension } from '@/utils/file'

type PreviewKind = 'image' | 'pdf' | 'audio' | 'video' | 'text'

// The formats Parrot signs an inline preview link for (`EXHIBIT_PREVIEW_CONTENT_TYPES`).
const PREVIEW_KINDS: Record<string, PreviewKind> = {
  jpg: 'image',
  jpeg: 'image',
  png: 'image',
  gif: 'image',
  webp: 'image',
  bmp: 'image',
  pdf: 'pdf',
  txt: 'text',
  mp3: 'audio',
  wav: 'audio',
  m4a: 'audio',
  aac: 'audio',
  ogg: 'audio',
  mp4: 'video',
  webm: 'video',
  mov: 'video',
}

const FULL_WIDTH_KINDS: (PreviewKind | undefined)[] = ['pdf', 'text']

const PREVIEW_HEIGHT = 'calc(100vh - 220px)'

export type PreviewFile = {
  title: string
  filename: string
  format: string
  previewUrl?: string | null
  downloadUrl?: string | null
}

const previewKind = (file: PreviewFile) =>
  file.previewUrl ? PREVIEW_KINDS[file.format.toLowerCase()] : undefined

export const canPreviewExhibit = (exhibit: CourtReporterExhibit) =>
  !!exhibit.file && previewKind(exhibitPreviewFile(exhibit)) !== undefined

export const exhibitPreviewFile = (
  exhibit: CourtReporterExhibit,
): PreviewFile => ({
  title: exhibit.name,
  filename: exhibit.file?.filename ?? '',
  format: exhibit.file?.format ?? '',
  previewUrl: exhibit.file?.preview_url,
  downloadUrl: exhibit.file?.url,
})

const ExhibitFilePreview: React.FC<{ file: PreviewFile }> = ({ file }) => {
  const [failed, setFailed] = useState(false)
  const kind = previewKind(file)
  const url = file.previewUrl ?? undefined
  const title = `Preview of ${file.filename}`

  if (!kind || failed) {
    return (
      <Center mih={200}>
        <Text size="sm" color="dimmed">
          {failed
            ? 'The preview could not be loaded.'
            : 'Preview is not supported for this file type.'}
        </Text>
      </Center>
    )
  }

  switch (kind) {
    case 'image':
      return (
        <Center>
          <img
            src={url}
            alt={title}
            className="max-w-full object-contain"
            style={{ maxHeight: PREVIEW_HEIGHT }}
            onError={() => setFailed(true)}
          />
        </Center>
      )
    case 'audio':
      return (
        <audio
          controls
          src={url}
          aria-label={title}
          className="w-full min-w-[400px]"
          onError={() => setFailed(true)}
        />
      )
    case 'video':
      return (
        <video
          controls
          src={url}
          aria-label={title}
          className="w-full"
          style={{ maxHeight: PREVIEW_HEIGHT }}
          onError={() => setFailed(true)}
        />
      )
    default:
      return (
        <iframe
          src={url}
          title={title}
          className="block w-full border-0"
          style={{ height: PREVIEW_HEIGHT }}
        />
      )
  }
}

export const ExhibitFilePreviewModal: React.FC<{
  file: PreviewFile | null
  onClose: () => void
}> = ({ file, onClose }) => (
  <Modal
    opened={file !== null}
    onClose={onClose}
    title={file?.title}
    // Images and media size the modal; documents need a fixed width to be readable.
    size={file && FULL_WIDTH_KINDS.includes(previewKind(file)) ? '90%' : 'auto'}
    yOffset={24}
  >
    {file && (
      <>
        <ExhibitFilePreview key={file.previewUrl} file={file} />
        {file.downloadUrl && (
          <Group position="right" mt="md">
            <Button
              component="a"
              href={file.downloadUrl}
              target="_blank"
              variant="default"
              leftIcon={<DownloadIcon />}
            >
              Download
            </Button>
          </Group>
        )}
      </>
    )}
  </Modal>
)

// A file not uploaded yet is previewed straight from the browser's copy of it.
export const LocalFilePreviewModal: React.FC<{
  file: File | null
  onClose: () => void
}> = ({ file, onClose }) => {
  const [url, setUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!file) {
      return
    }
    const objectUrl = URL.createObjectURL(file)
    setUrl(objectUrl)
    return () => {
      URL.revokeObjectURL(objectUrl)
      setUrl(null)
    }
  }, [file])

  return (
    <ExhibitFilePreviewModal
      file={
        file && url
          ? {
              title: file.name,
              filename: file.name,
              format: fileExtension(file.name),
              previewUrl: url,
            }
          : null
      }
      onClose={onClose}
    />
  )
}

export const canPreviewLocalFile = (file: File) =>
  PREVIEW_KINDS[fileExtension(file.name)] !== undefined
